import { Phone, Mail, MessageSquare, Users, FileText, MoreHorizontal } from 'lucide-react';
import type { Activity, ActivityType } from '../../types';

// Activity type icons
export const ACTIVITY_ICONS: Record<ActivityType, typeof Phone> = {
  CALL: Phone,
  EMAIL: Mail,
  TEXT: MessageSquare,
  IN_PERSON: Users,
  NOTE: FileText,
  OTHER: MoreHorizontal,
};

// Activity type colors
export const ACTIVITY_COLORS: Record<ActivityType, string> = {
  CALL: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  EMAIL: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  TEXT: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
  IN_PERSON: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
  NOTE: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  OTHER: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
};

// Email direction-specific colors for the icon circle
export const EMAIL_DIRECTION_COLORS = {
  INBOUND: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  OUTBOUND: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400',
};

// Meeting location display labels
export const MEETING_LOCATION_LABELS: Record<string, string> = {
  ON_CAMPUS: 'On Campus',
  CONFERENCE: 'Conference',
  RESTAURANT: 'Restaurant',
  GAME_TOURNAMENT: 'Game / Tournament',
  OTHER: 'Other',
};

// Format relative time
export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  // For older dates, show the actual date
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

// Format full timestamp for tooltip
export const formatFullTimestamp = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// Check if an email activity has synced metadata
export const isEmailWithMetadata = (activity: Activity): boolean => {
  return activity.type === 'EMAIL' && !!(activity.emailSubject || activity.gmailMessageId);
};
